import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Bell, Save } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function NotificationSettings({ user }) {
  const [preferences, setPreferences] = useState({
    homework_feedback: true,
    discussion_posts: true
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await fetch(`${API}/user/notification-preferences`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
      }
    } catch (error) {
      toast.error('Failed to load preferences');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/user/notification-preferences`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        toast.success('Preferences saved successfully!');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-10 h-10 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-4xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>Notification Settings</h1>
          </div>
          <p className="text-lg text-[#588157]">Manage your email notification preferences</p>
        </div>

        <Card className="p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
          <div className="space-y-6">
            <div className="flex items-center justify-between py-4 border-b border-[#CCD5AE]">
              <div className="flex-1">
                <Label htmlFor="homework-feedback" className="text-[#344E41] text-lg font-bold cursor-pointer">
                  Homework Feedback
                </Label>
                <p className="text-sm text-[#588157] mt-1">
                  Receive emails when teachers review and provide feedback on your homework submissions
                </p>
              </div>
              <Switch
                id="homework-feedback"
                checked={preferences.homework_feedback}
                onCheckedChange={(checked) => setPreferences({ ...preferences, homework_feedback: checked })}
                data-testid="homework-feedback-toggle"
                className="data-[state=checked]:bg-[#3A5A40]"
              />
            </div>

            <div className="flex items-center justify-between py-4">
              <div className="flex-1">
                <Label htmlFor="discussion-posts" className="text-[#344E41] text-lg font-bold cursor-pointer">
                  Discussion Posts
                </Label>
                <p className="text-sm text-[#588157] mt-1">
                  Receive emails when someone posts a new message in your course discussions
                </p>
              </div>
              <Switch
                id="discussion-posts"
                checked={preferences.discussion_posts}
                onCheckedChange={(checked) => setPreferences({ ...preferences, discussion_posts: checked })}
                data-testid="discussion-posts-toggle"
                className="data-[state=checked]:bg-[#3A5A40]"
              />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-[#CCD5AE]">
            <Button
              onClick={handleSave}
              disabled={loading}
              data-testid="save-preferences-button"
              className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg py-6 text-lg"
            >
              <Save className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {loading ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>

          <div className="mt-6 p-4 bg-[#E9EDC9] rounded-lg">
            <p className="text-sm text-[#344E41]">
              <strong>Note:</strong> Disabling notifications means you won't receive emails for these events. 
              You can always check updates by logging into your dashboard.
            </p>
          </div>
        </Card>
      </main>
    </div>
  );
}
